import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { useCreateDossierId, useGetDossier } from 'src/networking/terminals'
import {
  usePostPisPaymentCallback,
  usePutPisUpdateState,
} from 'src/networking/terminals/pis/pis.gates'
import {
  atomDossier,
  atomPaymentData,
  atomPisPaymentId,
  atomPolicy,
  atomSelectedProduct,
  selectorDossierInsured,
} from 'src/storage'
import { CallbackDates, Insured } from 'src/types'
import { mapDossier } from 'src/utils'

export const usePaymentSuccess = () => {
  const { t } = useTranslation()

  const resetDossier = useResetRecoilState(atomDossier)
  const resetPolicy = useResetRecoilState(atomPolicy)
  const resetSelectedProduct = useResetRecoilState(atomSelectedProduct)
  const insured = useRecoilValue(selectorDossierInsured)
  const saveDossier = useSetRecoilState(atomDossier)
  const setPaymentData = useSetRecoilState(atomPaymentData)
  const [pisPaymentId, setPisPaymentId] = useRecoilState(atomPisPaymentId)
  const [policy] = useRecoilState(atomPolicy)

  const { crossGate: getDossier } = useGetDossier()
  const { crossGate: createDossierId } = useCreateDossierId()

  const { crossGate: postPisPaymentCallback } = usePostPisPaymentCallback()
  const { crossGate: putPisUpdateState } = usePutPisUpdateState()

  const [isPisPayment, setIsPisPayment] = useState(true)
  const [callbackDates, setCallbackDates] = useState<CallbackDates | undefined>(undefined)

  useEffect(() => {
    const dataFine = policy?.dataFine
    const dataInizio = policy?.dataInizio
    setPaymentData(undefined)
    resetPolicy()
    resetSelectedProduct()
    if (
      !insured ||
      (insured && (!insured.idAssicurato || !insured.codiceAssociato || !insured.utenteOggettoId))
    ) {
      return
    }
    if (pisPaymentId) {
      setIsPisPayment(true)
      postPisPaymentCallback(
        {
          body: {
            transactionId: pisPaymentId,
          },
        },
        {
          onSuccess: (response) => {
            setPisPaymentId(undefined)
            if (response) {
              putPisUpdateState(
                {
                  uriParams: {
                    shopId: response.numPoli,
                  },
                },
                {
                  onSuccess: (response) => {
                    createDossier(insured)
                    setCallbackDates({
                      dataFine,
                      dataInizio: undefined,
                      dataEmissione: new Date(response.dataEffetto).getTime(),
                    })
                  },
                }
              )
            }
          },
          onFailure: () => {
            setPisPaymentId(undefined)
          },
        }
      )
    } else {
      setIsPisPayment(false)
      createDossier(insured)
      setCallbackDates({
        dataFine,
        dataInizio,
        dataEmissione: undefined,
      })
    }
  }, [])

  const createDossier = (insured: Insured) => {
    createDossierId(
      {
        body: {
          codiceAssociato: insured.codiceAssociato,
          associatoId: insured.idAssicurato,
          utenteOggettoId: insured.utenteOggettoId,
          productCode: null,
        },
      },
      {
        onSuccess: (newDossier) => {
          resetDossier()
          getDossier(
            {
              headers: {
                xDossierId: newDossier.xdossierId,
              },
            },
            {
              onSuccess: (newDossierCreated) => {
                const dossier = mapDossier(newDossierCreated)
                saveDossier(dossier)
              },
            }
          )
        },
      }
    )
  }

  return {
    t,
    isPisPayment,
    callbackDates,
  }
}
